import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Mail, Phone, MapPin, Clock, Facebook, Instagram, Linkedin, Youtube, Send, ExternalLink, CheckCircle2, Search, Upload, X as XIcon, FileText, Copy } from "lucide-react";
import { api, API, formatApiError } from "@/lib/api";

const SOC_ICONS = { facebook: Facebook, instagram: Instagram, linkedin: Linkedin, youtube: Youtube };

function OrderForm({ slug, accent, primary, widgetTitle, widgetSubtitle }) {
    const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", deadline_hint: "" });
    const [files, setFiles] = useState([]);  // browser File objects
    const [fileUrls, setFileUrls] = useState([""]);  // optional drive/wetransfer paste
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState(false);

    const addFile = (e) => {
        const list = Array.from(e.target.files || []);
        // hard cap 10 files, 25MB each for the form
        const filtered = list.filter(f => f.size <= 25 * 1024 * 1024);
        setFiles(prev => [...prev, ...filtered].slice(0, 10));
        e.target.value = "";
    };
    const removeFile = (i) => setFiles(prev => prev.filter((_, j) => j !== i));

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true); setError("");
        try {
            const hasFiles = files.length > 0;
            let data;
            if (hasFiles) {
                const fd = new FormData();
                fd.append("name", form.name);
                fd.append("message", form.message);
                if (form.email) fd.append("email", form.email);
                if (form.phone) fd.append("phone", form.phone);
                if (form.deadline_hint) fd.append("deadline_hint", form.deadline_hint);
                files.forEach(f => fd.append("files", f));
                const res = await api.post(`/public/landing/${slug}/order-multipart`, fd);
                data = res.data;
            } else {
                const payload = { ...form, file_urls: fileUrls.filter(u => u && u.trim()) };
                const res = await api.post(`/public/landing/${slug}/order`, payload);
                data = res.data;
            }
            setResult(data);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    const copyCode = () => {
        if (!result?.tracking_token) return;
        navigator.clipboard.writeText(result.tracking_token);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    if (result) {
        return (
            <div className="text-center py-6 max-w-xl mx-auto" data-testid="order-success-box">
                <CheckCircle2 size={56} className="mx-auto mb-3" style={{ color: "var(--green, #10B981)" }} />
                <h3 className="font-extrabold text-[24px]" style={{ color: primary }}>Richiesta ricevuta!</h3>
                <p className="text-[14px] mt-2 mb-5" style={{ color: "var(--text-2)" }}>Il tuo ordine è stato registrato. Salva questo codice per seguire lo stato del lavoro.</p>
                <div className="p-5 rounded-xl mb-4" style={{ background: "var(--bg)", border: "1px solid var(--line)" }}>
                    <div className="mono text-[10px] uppercase tracking-wider font-bold mb-1" style={{ color: "var(--muted)" }}>Numero ordine</div>
                    <div className="font-extrabold text-[26px] mono" style={{ color: accent }} data-testid="order-job-number">#{result.job_number}</div>
                    <div className="mt-4 pt-4" style={{ borderTop: "1px solid var(--line)" }}>
                        <div className="mono text-[10px] uppercase tracking-wider font-bold mb-1" style={{ color: "var(--muted)" }}>Codice di tracciamento</div>
                        <div className="flex items-center justify-center gap-2">
                            <code className="font-extrabold text-[16px] mono" data-testid="order-tracking-token">{result.tracking_token}</code>
                            <button type="button" onClick={copyCode} className="p-1.5 rounded hover:bg-black/5" data-testid="copy-tracking-btn" title="Copia codice">
                                <Copy size={14} style={{ color: primary }} />
                            </button>
                            {copied && <span className="text-[11px] font-bold" style={{ color: "var(--green, #10B981)" }}>Copiato!</span>}
                        </div>
                    </div>
                    {result.summary && <div className="text-[12.5px] mt-4 italic" style={{ color: "var(--text-2)" }}>&ldquo;{result.summary}&rdquo;</div>}
                </div>
                <div className="flex flex-col sm:flex-row gap-2 justify-center">
                    <a href={`/track/${result.tracking_token}`} className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-bold text-white text-[14px]" style={{ background: accent }} data-testid="go-to-tracking-btn">
                        Segui il tuo ordine →
                    </a>
                    <button type="button" onClick={() => { setResult(null); setForm({ name: "", email: "", phone: "", message: "", deadline_hint: "" }); setFiles([]); setFileUrls([""]); }} className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-bold text-[14px]" style={{ background: "var(--bg)", color: primary, border: `1px solid ${primary}33` }}>
                        Invia un altro ordine
                    </button>
                </div>
            </div>
        );
    }

    const setFileUrl = (i, v) => setFileUrls(p => p.map((u, j) => j === i ? v : u));
    const addUrlField = () => setFileUrls(p => [...p, ""]);

    return (
        <form onSubmit={submit} className="max-w-xl mx-auto space-y-3" data-testid="public-order-form">
            <input required value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Nome o ragione sociale *" className="input" data-testid="order-name-input" />
            <div className="grid md:grid-cols-2 gap-3">
                <input type="email" value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))} placeholder="Email" className="input" data-testid="order-email-input" />
                <input value={form.phone} onChange={(e) => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="Telefono" className="input" data-testid="order-phone-input" />
            </div>
            <textarea required rows={4} value={form.message} onChange={(e) => setForm(p => ({ ...p, message: e.target.value }))} placeholder="Cosa ti serve? Quantità, formato, dettagli... *" className="input" data-testid="order-message-input" />
            <input value={form.deadline_hint} onChange={(e) => setForm(p => ({ ...p, deadline_hint: e.target.value }))} placeholder="Per quando? (es. giovedì, fra 3 giorni)" className="input" data-testid="order-deadline-input" />

            {/* File uploads */}
            <div>
                <label className="cursor-pointer flex items-center gap-2 p-3 rounded-xl border-2 border-dashed hover:bg-black/5 transition" style={{ borderColor: `${primary}55`, color: primary }} data-testid="file-upload-label">
                    <Upload size={16} />
                    <span className="text-[13.5px] font-bold">Carica file (PDF, immagini, fino a 25MB)</span>
                    <input type="file" multiple onChange={addFile} className="hidden" accept=".pdf,.jpg,.jpeg,.png,.svg,.ai,.eps,.psd,.tif,.tiff" data-testid="file-upload-input" />
                </label>
                {files.length > 0 && (
                    <div className="mt-2 space-y-1.5">
                        {files.map((f, i) => (
                            <div key={i} className="flex items-center gap-2 p-2 rounded text-[12.5px]" style={{ background: "var(--bg)" }} data-testid={`file-item-${i}`}>
                                <FileText size={14} style={{ color: primary }} />
                                <span className="flex-1 truncate font-bold">{f.name}</span>
                                <span className="mono text-[11px]" style={{ color: "var(--muted)" }}>{(f.size / 1024 / 1024).toFixed(1)}MB</span>
                                <button type="button" onClick={() => removeFile(i)} className="text-red-500 hover:opacity-70" data-testid={`remove-file-${i}`}><XIcon size={14} /></button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Optional URL fields */}
            {files.length === 0 && (
                <details>
                    <summary className="cursor-pointer text-[12px] font-bold mb-1" style={{ color: "var(--muted)" }}>Oppure incolla link Drive/WeTransfer</summary>
                    <div className="mt-2 space-y-2">
                        {fileUrls.map((u, i) => (
                            <input key={i} value={u} onChange={(e) => setFileUrl(i, e.target.value)} placeholder={`https://… link file ${i + 1}`} className="input" data-testid={`url-input-${i}`} />
                        ))}
                        <button type="button" onClick={addUrlField} className="text-[12px] font-bold" style={{ color: primary }}>+ Aggiungi altro link</button>
                    </div>
                </details>
            )}

            {error && <div className="p-2 rounded text-[12.5px]" style={{ background: "rgba(239,68,68,.1)", color: "#B91C1C" }} data-testid="order-error">{error}</div>}
            <button disabled={loading} type="submit" className="w-full py-3 rounded-xl font-bold text-[14px] text-white flex items-center justify-center gap-2 transition hover:opacity-90 disabled:opacity-50" style={{ background: accent }} data-testid="order-submit-btn">
                {loading ? "Invio in corso..." : <><Send size={15} /> Invia richiesta</>}
            </button>
            <p className="text-[11px] text-center" style={{ color: "var(--muted)" }}>
                🤖 La tua richiesta viene letta dall&apos;AI e tracciata in tempo reale. Ricevi un codice per seguire lo stato.
            </p>
        </form>
    );
}

function TrackBlock({ accent, primary, title }) {
    const navigate = useNavigate();
    const [code, setCode] = useState("");
    const go = (e) => {
        e.preventDefault();
        const c = code.trim();
        if (c) navigate(`/track/${c}`);
    };
    return (
        <form onSubmit={go} className="max-w-xl mx-auto" data-testid="track-form">
            <div className="text-center mb-4">
                <h2 className="text-[22px] md:text-[28px] font-extrabold" style={{ color: primary }}>{title || "Hai già un ordine?"}</h2>
                <p className="text-[13.5px] mt-1" style={{ color: "var(--text-2)" }}>Inserisci il tuo codice di tracciamento per vedere lo stato.</p>
            </div>
            <div className="flex gap-2">
                <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Codice di tracciamento" className="input flex-1" data-testid="track-code-input" />
                <button type="submit" className="px-5 py-3 rounded-xl font-bold text-white text-[13.5px] flex items-center gap-2" style={{ background: primary }} data-testid="track-submit-btn">
                    <Search size={15} /> Cerca
                </button>
            </div>
        </form>
    );
}

export default function PublicLanding() {
    const { slug } = useParams();
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/public/landing/${slug}`);
                setData(data);
            } catch (e) {
                setErr(formatApiError(e));
            }
        })();
    }, [slug]);

    if (err) return <div className="min-h-screen grid place-items-center p-6 text-center"><div><h1 className="text-[24px] font-bold">404</h1><p className="text-[14px]" style={{ color: "var(--muted)" }}>{err}</p></div></div>;
    if (!data) return <div className="min-h-screen grid place-items-center"><div className="mono text-[12px]" style={{ color: "var(--muted)" }}>Caricamento...</div></div>;

    const { tenant_name, landing, contact_info } = data;
    const primary = landing.primary_color || "#1E40AF";
    const accent = landing.accent_color || "#FF1A5C";
    const widgets = (landing.widgets || []).filter(w => w.enabled).sort((a, b) => a.order - b.order);

    return (
        <div className="min-h-screen" style={{ background: "#fff", color: "#0D1421" }}>
            {/* Header */}
            <header className="px-6 md:px-12 py-5 flex items-center justify-between" style={{ borderBottom: "1px solid var(--line)" }}>
                <div className="flex items-center gap-3">
                    {landing.logo_url ? <img src={landing.logo_url} alt="" className="h-10" /> : <div className="w-10 h-10 rounded-lg grid place-items-center text-white font-extrabold" style={{ background: primary }}>{tenant_name[0]}</div>}
                    <span className="font-extrabold text-[18px]" style={{ color: primary }} data-testid="tenant-name">{tenant_name}</span>
                </div>
                <div className="flex items-center gap-2">
                    <a href="#track" className="hidden md:inline-flex items-center gap-1.5 px-3 py-2 rounded-full font-bold text-[12.5px]" style={{ color: primary, background: `${primary}11` }} data-testid="header-track-link">
                        <Search size={13} /> Traccia ordine
                    </a>
                    {contact_info.phone_mobile && (
                        <a href={`tel:${contact_info.phone_mobile}`} className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-full font-bold text-[13px] text-white" style={{ background: accent }}>
                            <Phone size={13} /> {contact_info.phone_mobile}
                        </a>
                    )}
                </div>
            </header>

            {/* Widgets */}
            {widgets.map(w => {
                if (w.type === "hero") {
                    return (
                        <section key={w.id} className="px-6 md:px-12 py-16 md:py-24 text-center relative overflow-hidden" style={{ background: w.data.image ? `linear-gradient(rgba(13,20,33,.6), rgba(13,20,33,.6)), url(${w.data.image}) center/cover` : `linear-gradient(135deg, ${primary} 0%, ${accent} 100%)`, color: "#fff" }} data-testid="hero-section">
                            <h1 className="text-[40px] md:text-[56px] font-extrabold tracking-tight max-w-3xl mx-auto leading-[1.1]">{w.data.title}</h1>
                            <p className="text-[16px] md:text-[18px] mt-4 max-w-2xl mx-auto opacity-90">{w.data.subtitle}</p>
                            <a href="#contact" className="inline-flex items-center gap-2 mt-8 px-7 py-3 rounded-full font-bold text-[14px]" style={{ background: "#fff", color: primary }} data-testid="hero-cta-btn">
                                Richiedi ora <Send size={14} />
                            </a>
                        </section>
                    );
                }
                if (w.type === "offers") {
                    return (
                        <section key={w.id} className="px-6 md:px-12 py-16">
                            <h2 className="text-[28px] md:text-[34px] font-extrabold text-center mb-10" style={{ color: primary }}>{w.data.title}</h2>
                            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 max-w-6xl mx-auto">
                                {(w.data.items || []).map((it, i) => (
                                    <a key={i} href={it.link || "#contact"} className="rounded-2xl overflow-hidden hover:-translate-y-1 transition-all" style={{ background: "#fff", border: "1px solid var(--line)", aspectRatio: "3/2", display: "flex", flexDirection: "column" }} data-testid={`offer-card-${i}`}>
                                        <div className="flex-1 relative" style={{ background: it.image ? `url(${it.image}) center/cover` : `linear-gradient(135deg, ${primary}22, ${accent}33)` }}>
                                            {it.price && <span className="absolute top-3 right-3 px-3 py-1 rounded-full font-extrabold text-[14px] text-white" style={{ background: accent }}>{it.price}</span>}
                                        </div>
                                        <div className="p-3 md:p-4">
                                            <div className="font-extrabold text-[14px] md:text-[16px] leading-tight" style={{ color: primary }}>{it.title}</div>
                                            {it.subtitle && <div className="text-[11.5px] md:text-[12.5px] mt-1" style={{ color: "var(--muted)" }}>{it.subtitle}</div>}
                                        </div>
                                    </a>
                                ))}
                            </div>
                        </section>
                    );
                }
                if (w.type === "text") {
                    return (
                        <section key={w.id} className="px-6 md:px-12 py-14 max-w-3xl mx-auto text-center">
                            <h2 className="text-[26px] md:text-[32px] font-extrabold mb-5" style={{ color: primary }}>{w.data.title}</h2>
                            <p className="text-[15px] md:text-[16px] leading-relaxed whitespace-pre-wrap" style={{ color: "var(--text-2)" }}>{w.data.body}</p>
                        </section>
                    );
                }
                if (w.type === "gallery" && (w.data.images || []).length > 0) {
                    return (
                        <section key={w.id} className="px-6 md:px-12 py-14">
                            <h2 className="text-[26px] md:text-[32px] font-extrabold text-center mb-8" style={{ color: primary }}>{w.data.title}</h2>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-6xl mx-auto">
                                {w.data.images.map((src, i) => <img key={i} src={src} alt="" className="w-full aspect-square object-cover rounded-xl" />)}
                            </div>
                        </section>
                    );
                }
                if (w.type === "contact") {
                    return (
                        <section key={w.id} id="contact" className="px-6 md:px-12 py-16" style={{ background: "var(--bg)" }} data-testid="contact-section">
                            <h2 className="text-[26px] md:text-[32px] font-extrabold text-center mb-2" style={{ color: primary }}>{w.data.title || "Richiedi un preventivo"}</h2>
                            <p className="text-[14px] text-center mb-8" style={{ color: "var(--text-2)" }}>{w.data.subtitle || "Compila il form, ti rispondiamo subito"}</p>
                            <OrderForm slug={slug} accent={accent} primary={primary} widgetTitle={w.data.title} widgetSubtitle={w.data.subtitle} />
                        </section>
                    );
                }
                if (w.type === "info") {
                    return (
                        <section key={w.id} className="px-6 md:px-12 py-14 max-w-4xl mx-auto">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-5 text-center">
                                {w.data.show_phone && (contact_info.phone_mobile || contact_info.phone_fixed) && (
                                    <a href={`tel:${contact_info.phone_mobile || contact_info.phone_fixed}`} className="block">
                                        <Phone size={22} className="mx-auto mb-2" style={{ color: accent }} />
                                        <div className="font-bold text-[13.5px]" style={{ color: primary }}>{contact_info.phone_mobile || contact_info.phone_fixed}</div>
                                    </a>
                                )}
                                {w.data.show_email && contact_info.email && (
                                    <a href={`mailto:${contact_info.email}`} className="block">
                                        <Mail size={22} className="mx-auto mb-2" style={{ color: accent }} />
                                        <div className="font-bold text-[13.5px]" style={{ color: primary }}>{contact_info.email}</div>
                                    </a>
                                )}
                                {w.data.show_address && contact_info.address && (
                                    <div>
                                        <MapPin size={22} className="mx-auto mb-2" style={{ color: accent }} />
                                        <div className="font-bold text-[13.5px]" style={{ color: primary }}>{contact_info.address}<br />{contact_info.cap} {contact_info.city}</div>
                                    </div>
                                )}
                                {w.data.show_hours && w.data.hours && (
                                    <div>
                                        <Clock size={22} className="mx-auto mb-2" style={{ color: accent }} />
                                        <div className="font-bold text-[13.5px]" style={{ color: primary }}>{w.data.hours}</div>
                                    </div>
                                )}
                            </div>
                        </section>
                    );
                }
                if (w.type === "social") {
                    const socials = Object.entries(landing.social || {}).filter(([, v]) => v);
                    if (!socials.length) return null;
                    return (
                        <section key={w.id} className="px-6 py-10 text-center">
                            <div className="flex justify-center gap-3">
                                {socials.map(([name, url]) => {
                                    const Icon = SOC_ICONS[name] || ExternalLink;
                                    return (
                                        <a key={name} href={url} target="_blank" rel="noreferrer" className="w-12 h-12 rounded-full grid place-items-center transition hover:scale-110" style={{ background: primary, color: "#fff" }}>
                                            <Icon size={18} />
                                        </a>
                                    );
                                })}
                            </div>
                        </section>
                    );
                }
                return null;
            })}

            {/* Tracking block — always visible */}
            <section id="track" className="px-6 md:px-12 py-14" style={{ background: `${primary}06`, borderTop: "1px solid var(--line)" }} data-testid="tracking-section">
                <TrackBlock accent={accent} primary={primary} />
            </section>

            {/* Footer */}
            <footer className="px-6 md:px-12 py-8 text-center" style={{ borderTop: "1px solid var(--line)", background: "#0D1421", color: "#A5B0CC" }}>
                <div className="mono text-[10.5px] uppercase tracking-[0.15em]">
                    Powered by{" "}
                    <a href="/totalpl" target="_blank" className="font-extrabold hover:underline" style={{ color: "#fff" }}>
                        Total<span style={{ background: `linear-gradient(135deg, #FF1A5C, #FF6B1A, #FFCB05)`, WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent" }}>PL</span>
                    </a>
                </div>
                <div className="text-[11px] mt-2 opacity-60">© {new Date().getFullYear()} {contact_info.company_name} · {tenant_name}</div>
            </footer>
        </div>
    );
}
